import { useRef, useState } from 'react';
import type { ClientScope, CreatedClientSecret } from '@baex/shared';
import { validateClientId, validateDisplayName } from '@baex/shared';
import { isApiError } from '../../api/client';
import { cx } from '../../lib/cx';
import { Button } from '../../components/ui/Button';
import { ErrorNotice } from '../../components/ui/ErrorNotice';
import { TextAreaField, TextField } from '../../components/ui/Field';
import { Modal } from '../../components/ui/Modal';
import { focusFirstInvalid } from '../../lib/focusFirstInvalid';
import { NOTES_MAX_LENGTH, notesError as checkNotes } from './clientRules';
import { SecretPanel } from './SecretPanel';

export const CLIENT_ID_TAKEN_MESSAGE = 'This Client ID is already in use.';

/** One complete set of classes per state — see the note at the Scope fieldset below. */
function scopeCardClass(selected: boolean): string {
  return cx(
    'flex cursor-pointer gap-2 rounded border p-2.5 transition-colors',
    selected
      ? 'border-primary bg-primary-soft'
      : 'border-hairline bg-surface hover:bg-surface-sunken',
  );
}

export interface CreateClientModalProps {
  open: boolean;
  onCancel: () => void;
  onCreate: (input: {
    clientId: string;
    displayName: string;
    scope: ClientScope;
    notes: string;
  }) => Promise<CreatedClientSecret>;
  /** Called from step 2's "Done, I copied it". */
  onDone: (clientId: string) => void;
}

/** One modal, two steps (see the "Create a new client" section) — the form, then the key, in place. */
export function CreateClientModal({ open, onCancel, onCreate, onDone }: CreateClientModalProps) {
  const [clientId, setClientId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [scope, setScope] = useState<ClientScope>('own');
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState<string | undefined>(undefined);
  const [clientIdError, setClientIdError] = useState<string | undefined>(undefined);
  const [displayNameError, setDisplayNameError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [secret, setSecret] = useState<CreatedClientSecret | null>(null);

  const clientIdRef = useRef<HTMLInputElement | null>(null);
  const displayNameRef = useRef<HTMLInputElement | null>(null);
  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  function reset() {
    setClientId('');
    setDisplayName('');
    setScope('own');
    setNotes('');
    setNotesError(undefined);
    setClientIdError(undefined);
    setDisplayNameError(undefined);
    setSubmitError(null);
    setSubmitting(false);
    setSecret(null);
  }

  function checkClientId(value: string): string | undefined {
    const result = validateClientId(value);
    if (result.ok) return undefined;
    // The spec's wording for the character rule (see the "Create a new client" section).
    return value.length === 0
      ? 'Client ID is required.'
      : 'Only lowercase letters, digits, and dashes are allowed (e.g. client-acme).';
  }

  function checkDisplayName(value: string): string | undefined {
    const result = validateDisplayName(value);
    return result.ok ? undefined : result.reason;
  }

  async function submit() {
    // Every field is re-checked here, not just the ones that were blurred: an untouched
    // empty form is the case that used to give no feedback whatsoever (see the "Admin Interface" section).
    const nextClientIdError = checkClientId(clientId);
    const nextDisplayNameError = checkDisplayName(displayName);
    const nextNotesError = checkNotes(notes);
    setClientIdError(nextClientIdError);
    setDisplayNameError(nextDisplayNameError);
    setNotesError(nextNotesError);
    const atFault = focusFirstInvalid([
      { error: nextClientIdError, ref: clientIdRef },
      { error: nextDisplayNameError, ref: displayNameRef },
      { error: nextNotesError, ref: notesRef },
    ]);
    if (atFault) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await onCreate({ clientId, displayName, scope, notes });
      setSecret(created);
    } catch (error) {
      // A Client ID taken a second earlier stays on step 1 with the inline message, and
      // takes focus the same way a client-side failure does — this is the case where the
      // reader most wants to edit one character and try again, so leaving focus adrift
      // would make them hunt for the field the message belongs to (see the "Admin Interface" section).
      const takenNow =
        isApiError(error) &&
        (error.status === 409 ||
          error.code === 'CONFLICT' ||
          (error.code === 'VALIDATION_ERROR' && /already in use/i.test(error.message)));

      if (takenNow) {
        setClientIdError(CLIENT_ID_TAKEN_MESSAGE);
        focusFirstInvalid([{ error: CLIENT_ID_TAKEN_MESSAGE, ref: clientIdRef }]);
      } else {
        setSubmitError(error);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (secret) {
    // Step 2: no close control, no Cancel — the client already exists.
    return (
      <Modal
        open={open}
        title={`Client "${secret.clientId}" created`}
        stepLabel="Step 2 of 2"
        footer={
          <Button
            variant="primary"
            onClick={() => {
              const created = secret.clientId;
              reset();
              onDone(created);
            }}
          >
            Done, I copied it
          </Button>
        }
      >
        <SecretPanel secret={secret} />
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="New Client"
      stepLabel="Step 1 of 2"
      onClose={() => {
        reset();
        onCancel();
      }}
      footer={
        <>
          <Button
            onClick={() => {
              reset();
              onCancel();
            }}
          >
            Cancel
          </Button>
          {/*
            The only legitimate `disabled` (see the "Admin Interface" section): the request this button started is
            still running, so it cannot be pressed twice. Incomplete input never switches
            it off — pressing it then is what produces the messages.
          */}
          <Button variant="primary" disabled={submitting} onClick={() => void submit()}>
            {submitting ? 'Creating…' : 'Create client →'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          ref={clientIdRef}
          label="Client ID"
          value={clientId}
          error={clientIdError}
          hint="The client's permanent identity: it names their folder on the server, it is half of the credential they authenticate with, and it can never be changed. Lowercase letters, digits and dashes, e.g. client-acme. Agree it with the integration owner before creating it."
          onChange={(event) => {
            setClientId(event.target.value);
            if (clientIdError) setClientIdError(checkClientId(event.target.value));
          }}
          onBlur={(event) => setClientIdError(checkClientId(event.target.value))}
        />

        <TextField
          ref={displayNameRef}
          label="Display name"
          hint="How this client is labelled in this dashboard, for people. Free text — the partner's real name, e.g. Acme Logistics Ltd. It is never sent anywhere and never used to address a folder, so it can be changed without affecting anything."
          value={displayName}
          error={displayNameError}
          onChange={(event) => {
            setDisplayName(event.target.value);
            if (displayNameError) setDisplayNameError(checkDisplayName(event.target.value));
          }}
          onBlur={(event) => setDisplayNameError(checkDisplayName(event.target.value))}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[12px] font-medium text-ink-muted">Scope</legend>

          {/*
            The chosen option is drawn as chosen, not left to an 8px dot to say so. Scope
            is the one setting on this form that decides whether a partner's key can reach
            another partner's folder and it cannot be changed afterwards, so which of the
            two is selected has to be legible from across the dialog. Exclusive class sets
            again: a `hover:` background layered over a selected background is decided by
            the stylesheet's order, not the call site's.
          */}
          <label className={scopeCardClass(scope === 'own')}>
            <input
              type="radio"
              name="scope"
              className="form-radio mt-0.5 text-primary focus:ring-0"
              checked={scope === 'own'}
              onChange={() => setScope('own')}
            />
            <span>
              <span className="block text-[13px] text-ink">Own folder only</span>
              <span className="block text-2xs text-ink-muted">
                This client can only read/write its own folder ({clientId || 'its Client ID'}). Use
                this for every external partner. Cannot be changed to reach another client&apos;s
                folder, even by mistake.
              </span>
            </span>
          </label>

          <label className={scopeCardClass(scope === 'any')}>
            <input
              type="radio"
              name="scope"
              className="form-radio mt-0.5 text-primary focus:ring-0"
              checked={scope === 'any'}
              onChange={() => setScope('any')}
            />
            <span>
              <span className="block text-[13px] text-ink">
                Any client&apos;s folder (internal use only)
              </span>
              <span className="block text-2xs text-ink-muted">
                This client must name a target client on every call, and can read/write ANY
                client&apos;s folder. Only for our own internal apps — never assign to an external
                partner.
              </span>
            </span>
          </label>

          <p className="text-2xs text-ink-subtle">
            Scope is fixed at creation. If it ever needs to change, delete the client and create a
            new Client ID so the change shows up in the audit trail.
          </p>
        </fieldset>

        <div>
          <TextAreaField
            ref={notesRef}
            label="Notes (optional)"
            rows={3}
            value={notes}
            error={notesError}
            hint="Who requested it, contact person, ticket reference."
            onChange={(event) => {
              setNotes(event.target.value);
              setNotesError(checkNotes(event.target.value));
            }}
            onBlur={(event) => setNotesError(checkNotes(event.target.value))}
          />
          <p
            className={`mt-1 text-right text-2xs ${
              notes.length > NOTES_MAX_LENGTH ? 'text-danger' : 'text-ink-subtle'
            }`}
          >
            {notes.length} / {NOTES_MAX_LENGTH}
          </p>
        </div>

        {submitError ? (
          <ErrorNotice error={submitError} fallback="The client could not be created." />
        ) : null}

        <p className="text-2xs text-ink-muted">
          Next step: we&apos;ll generate this client&apos;s API key for you to copy.
        </p>
      </div>
    </Modal>
  );
}
